import { ApiError, changeNow } from './api'
import { rememberedEach } from './done-extras'
import { lineDrawing, type Drawing } from './picture-tiles'
import { currencySymbol } from './round-config'
import { currentTrade } from './trade'
import { clear, el, money } from './ui'
export interface BreakdownLine { name: string; count: number; each_pence: number }
export interface BreakdownVisit { id: number; version: number; date: string; label: string; amount_pence: number; paid_pence: number; money_rev: number; lines: BreakdownLine[]; fixed: BreakdownLine[]; plain_lines: { clean_id: number; date: string; label: string; pence: number }[]; price_change_blocked?: string }

type Row = { name: string; count: number; text: string; custom?: boolean }
type Prices = Partial<Record<'windows' | 'roof' | 'gutters' | 'conservatory', number>>
const pence = (text: string): number | null => {
  if (!/^\d+(?:\.\d{1,2})?$/.test(text.trim())) return null
  const n = Math.round(Number(text) * 100)
  return Number.isSafeInteger(n) && n <= 1_000_000 ? n : null
}
const sum = (lines: BreakdownLine[]): number => lines.reduce((s, l) => s + l.count * l.each_pence, 0)

// Drafts live only in this invoice/payment step. Folding retains them; closing discards unsaved changes.
export function invoiceBreakdown(visits: BreakdownVisit[], prices: Prices, opts: {
  draft?: boolean
  onChange?: () => void
  onToggle?: (open: boolean) => void
  onPreview: (lines: Map<number, BreakdownLine[]>) => void
  onVisitSaved?: (visit: { id: number; version: number }) => void
  onSaved: () => void
  onLocked: (error: unknown) => void
  changedWords?: string
}) {
  const node = el('section', { class: 'invoice-breakdown', 'data-invoice-breakdown': '' })
  const toggle = el('button', { type: 'button', class: 'link-button', 'aria-expanded': 'false' }, 'Show the breakdown')
  const panel = el('div', { class: 'invoice-breakdown-panel', hidden: '' })
  node.append(toggle, panel)
  const groups = visits.map(v => ({ visit: { ...v }, rows: v.lines.map(l => ({ name: l.name, count: l.count, text: (l.each_pence / 100).toFixed(2), custom: true } as Row)), original: JSON.stringify(v.lines), key: null as { body: string; key: string } | null }))
  const tileHints: (() => void)[] = []
  let open = false, saving = false
  const error = el('p', { class: opts.draft ? 'invoice-breakdown-error' : 'form-error', role: 'alert' })
  const save = el('button', { type: 'button', class: 'btn-secondary' }, 'Save breakdown') as HTMLButtonElement
  const linesOf = (rows: Row[]): BreakdownLine[] => rows.map(r => ({ name: r.name.trim(), count: r.count, each_pence: pence(r.text) ?? 0 }))
  const dirty = (): boolean => groups.some(g => JSON.stringify(linesOf(g.rows)) !== g.original || g.rows.some(r => pence(r.text) === null))
  const problem = (): string | null => {
    for (const g of groups) {
      if (g.visit.price_change_blocked && g.rows.length && !g.rows.some(r => pence(r.text) === null) && sum(linesOf(g.rows)) + sum(g.visit.fixed) !== g.visit.amount_pence) return g.visit.price_change_blocked
      for (const r of g.rows) {
        if (!r.name.trim() || r.name.trim().length > 60 || /[\u0000-\u001f\u007f\u2014]/.test(r.name)) return 'Name each line in up to 60 letters, without long dashes.'
        if (pence(r.text) === null) return `Put in a price each for ${r.name || 'the line'}, 0 if free.`
      }
      if (g.rows.length && (sum(linesOf(g.rows)) + sum(g.visit.fixed) < 1 || sum(linesOf(g.rows)) + sum(g.visit.fixed) > 100_000_000)) return `The ${opts.draft ? 'quote' : 'visit'} total must be above zero and no more than ${money(100_000_000)}.`
    }
    return null
  }
  const update = (): void => {
    save.disabled = saving || !dirty() || !!problem()
    error.textContent = problem() ?? ''
    opts.onChange?.()
    if (!problem()) opts.onPreview(new Map(groups.filter(g => g.rows.length || g.original !== '[]').map(g => [g.visit.id, g.rows.length ? [...linesOf(g.rows), ...g.visit.fixed] : []])))
  }
  toggle.addEventListener('click', () => {
    open = !open; panel.hidden = !open
    toggle.setAttribute('aria-expanded', String(open)); toggle.textContent = open ? 'Hide the breakdown' : 'Show the breakdown'
    node.closest('.invoice-step')?.classList.toggle('invoice-breakdown-open', open)
    opts.onToggle?.(open)
  })
  for (const g of groups) {
    const box = el('section', { class: 'invoice-breakdown-visit', 'data-clean-id': String(g.visit.id) })
    const tiles = el('div', { class: 'invoice-breakdown-tiles', role: 'group', 'aria-label': opts.draft ? 'Tap what the work is' : 'Tap what you did' })
    const rows = el('div', { class: 'invoice-breakdown-rows' })
    const totals = el('div', { class: 'invoice-breakdown-totals', 'aria-live': 'polite' })
    const total = (): number => g.rows.length ? sum(linesOf(g.rows)) + sum(g.visit.fixed) : g.visit.amount_pence
    const drawTotals = (): void => {
      clear(totals)
      const valid = !g.rows.some(r => pence(r.text) === null)
      for (const [label, amount] of (opts.draft ? [['Total', valid ? money(total()) : 'Price needed']] : [['Total', valid ? money(total()) : 'Price needed'], ['Already paid', money(g.visit.paid_pence)], ['Still to pay', valid ? money(Math.max(0, total() - g.visit.paid_pence)) : 'Price needed']])) totals.append(el('div', {}, el('span', {}, label!), el('strong', {}, amount!)))
      if (!opts.draft && valid && total() !== g.visit.amount_pence) totals.append(el('p', { class: 'invoice-breakdown-note' }, `Current price ${money(g.visit.amount_pence)}. Saving changes this visit to ${money(total())}. Your usual prices stay the same.`))
    }
    const drawRows = (): void => {
      clear(rows)
      for (const r of g.rows) {
        const row = el('div', { class: 'invoice-breakdown-row' })
        const title = r.custom ? el('input', { type: 'text', maxlength: '60', 'aria-label': opts.draft ? 'What the work is' : 'What you did', value: r.name }) as HTMLInputElement : el('strong', {}, r.name)
        if (r.custom) title.addEventListener('input', () => { r.name = (title as HTMLInputElement).value; drawTotals(); update() })
        const minus = el('button', { type: 'button', 'aria-label': `One fewer ${r.name}` }, '−') as HTMLButtonElement
        const plus = el('button', { type: 'button', 'aria-label': `One more ${r.name}` }, '+') as HTMLButtonElement
        const count = el('strong', {}, String(r.count))
        const each = el('input', { type: 'text', inputmode: 'decimal', 'aria-label': `Each ${r.name}`, value: r.text }) as HTMLInputElement
        const amount = el('strong', { class: 'invoice-breakdown-amount' })
        const refresh = (): void => { count.textContent = String(r.count); minus.disabled = r.count <= 1; plus.disabled = r.count >= 99; amount.textContent = pence(r.text) === null ? 'Price needed' : money(r.count * pence(r.text)!); drawTotals(); update() }
        minus.addEventListener('click', () => { r.count = Math.max(1, r.count - 1); refresh() })
        plus.addEventListener('click', () => { r.count = Math.min(99, r.count + 1); refresh() })
        each.addEventListener('input', () => { r.text = each.value; refresh() })
        const remove = el('button', { type: 'button', class: 'link-button', 'aria-label': `Remove ${r.name}` }, 'Remove')
        remove.addEventListener('click', () => { g.rows.splice(g.rows.indexOf(r), 1); drawRows(); drawTotals(); update() })
        row.append(title, el('div', { class: 'invoice-breakdown-count', 'aria-label': 'How many' }, minus, count, plus), el('label', {}, `Each ${currencySymbol()}`, each), amount, remove)
        rows.append(row); refresh()
      }
      if (g.visit.fixed.length) rows.append(el('p', { class: 'muted' }, 'Already on this visit. Change these in Edit visit.'))
      for (const l of g.visit.fixed) rows.append(el('div', { class: 'invoice-breakdown-fixed' }, el('span', {}, `${l.name} · ${l.count} at ${money(l.each_pence)}`), el('strong', {}, money(l.count * l.each_pence))))
    }
    const choices: [string, Drawing, number | undefined][] = currentTrade() === 'window_cleaning' ? [
      ['Window panels', 'panels', prices.windows], ['Roof windows', 'roofWindows', prices.roof],
      ['Conservatory roof', 'roof', prices.conservatory ?? rememberedEach('conservatory') ?? undefined],
      ['Gutters', 'gutters', prices.gutters ?? rememberedEach('gutters') ?? undefined],
      ['Frames and sills', 'frames', undefined], ['Fascias', 'fascias', undefined], ['Something else', 'dots', undefined],
    ] : [['Something else', 'dots', undefined]]
    for (const [name, drawing, price] of choices) {
      const seed = (): number | undefined => name === 'Window panels' ? prices.windows : name === 'Roof windows' ? prices.roof
        : name === 'Conservatory roof' ? prices.conservatory ?? rememberedEach('conservatory') ?? undefined
        : name === 'Gutters' ? prices.gutters ?? rememberedEach('gutters') ?? undefined : price
      const hint = el('small')
      const refreshHint = (): void => { const current = seed(); hint.textContent = current === undefined ? 'You say' : `${money(current)} each` }
      tileHints.push(refreshHint); refreshHint()
      const tile = el('button', { type: 'button', class: 'invoice-breakdown-tile' }, el('span', { class: 'invoice-breakdown-ring' }, lineDrawing(drawing, 24)), el('strong', {}, name), hint)
      tile.addEventListener('click', () => {
        if (g.rows.length >= 12) { error.textContent = `At most 12 breakdown lines per ${opts.draft ? 'quote' : 'visit'}.`; return }
        const price = seed()
        g.rows.push({ name: name === 'Something else' ? '' : name, count: 1, text: price === undefined ? '' : (price / 100).toFixed(2), custom: name === 'Something else' })
        drawRows(); drawTotals(); update()
      })
      tiles.append(tile)
    }
    box.append(el('h3', {}, g.visit.label), el('p', { class: 'muted' }, opts.draft ? 'Tap what the work is. These lines are the whole quote. Your usual prices stay the same.' : 'Tap what you did. These lines describe this visit only.'), tiles, rows, totals)
    panel.append(box); drawRows(); drawTotals()
  }
  const confirmation = el('div', { class: 'invoice-breakdown-confirm' })
  panel.append(error, confirmation)
  if (!opts.draft) panel.append(save)
  save.addEventListener('click', () => void persist())
  let approved: { id: number; total: number; paid: number } | null = null
  async function persist(): Promise<void> {
    if (saving || problem()) return
    saving = true; update(); clear(confirmation)
    for (const b of node.querySelectorAll<HTMLButtonElement | HTMLInputElement>('button, input')) b.disabled = true
    try {
      for (const g of groups) {
        const lines = linesOf(g.rows)
        if (JSON.stringify(lines) === g.original) continue
        const total = lines.length ? sum(lines) + sum(g.visit.fixed) : g.visit.amount_pence
        const body = { version: g.visit.version, breakdown_json: lines.length ? lines : null, breakdown_money_rev: g.visit.money_rev,
          ...(approved?.id === g.visit.id ? { breakdown_confirm: { paid_pence: approved.paid, total_pence: approved.total } } : {}) }
        const encoded = JSON.stringify(body)
        if (!g.key || g.key.body !== encoded) g.key = { body: encoded, key: crypto.randomUUID() }
        const answer = await changeNow<{ row: { version: number; _breakdown_rev: number } }>('PATCH', `/api/cleans/${g.visit.id}`, body, g.key.key)
        g.original = JSON.stringify(lines); g.visit.version = answer.row.version; g.visit.amount_pence = total
        opts.onVisitSaved?.({ id: g.visit.id, version: answer.row.version })
        for (const other of groups) other.visit.money_rev = answer.row._breakdown_rev
        approved = null; g.key = null
        window.dispatchEvent(new CustomEvent('dropby:data-changed'))
      }
      opts.onSaved()
    } catch (e) {
      if (e instanceof ApiError && e.code === 'breakdown_confirm') {
        const group = groups.find(g => JSON.stringify(linesOf(g.rows)) !== g.original)
        const id = group?.visit.id
        const paid = Number(e.body?.paid_pence), total = Number(e.body?.total_pence)
        if (id !== undefined && Number.isSafeInteger(paid) && Number.isSafeInteger(total)) {
          confirmation.append(el('p', {}, `${group!.visit.label}. ${e.message}`))
          const yes = el('button', { type: 'button', class: 'btn-secondary' }, `Confirm total ${money(total)}`)
          yes.addEventListener('click', () => { approved = { id, total, paid }; void persist() })
          confirmation.append(yes)
          return
        }
      }
      if (e instanceof ApiError && e.code === 'on_invoice') { opts.onLocked(e); return }
      error.textContent = e instanceof Error ? e.message : 'The breakdown did not save. Try again.'
      if (e instanceof ApiError && e.status === 409) error.textContent += opts.changedWords ?? ' Close this invoice and open it again to check the saved amounts. Earlier saved visits stay saved.'
    } finally {
      saving = false
      for (const b of node.querySelectorAll<HTMLButtonElement | HTMLInputElement>('button, input')) b.disabled = false
      save.disabled = !dirty() || !!problem()
    }
  }
  update()
  return { node, dirty, busy: () => saving, problem, lines: () => linesOf(groups[0]?.rows ?? []),
    setPrices: (next: Prices): void => { Object.assign(prices, { windows: undefined, roof: undefined, gutters: undefined, conservatory: undefined }, next); for (const refresh of tileHints) refresh() } }
}
